import { useState } from "react";
import { AnnotationStatusBadge } from "../AnnotationStatusBadge/AnnotationStatusBadge";
import type { EvalAnnotation, EvalFailureModeTag } from "../../types";
import { FAILURE_MODE_OPTIONS } from "../../utils/failureModeLabels";
import {
  canApproveOrReject,
  canCreateAnnotation,
  canStartReview,
  isTerminalAnnotationStatus,
} from "../../utils/annotationTransitions";
import { EvalAnnotationStatusValues } from "../../types";
import styles from "./AnnotationReviewPanel.module.css";

export interface AnnotationReviewPanelProps {
  annotation?: EvalAnnotation;
  isLoading?: boolean;
  isSubmitting?: boolean;
  isCreating?: boolean;
  errorMessage?: string | null;
  reviewerIdentity: string;
  onCreateAndStartReview: () => void;
  onStartReview: () => void;
  onApprove: (input: {
    reviewerNotes: string;
    failureModeTags: EvalFailureModeTag[];
  }) => void;
  onReject: (input: {
    reviewerNotes: string;
    failureModeTags: EvalFailureModeTag[];
  }) => void;
}

export function AnnotationReviewPanel({
  annotation,
  isLoading = false,
  isSubmitting = false,
  isCreating = false,
  errorMessage,
  reviewerIdentity,
  onCreateAndStartReview,
  onStartReview,
  onApprove,
  onReject,
}: AnnotationReviewPanelProps) {
  const [reviewerNotes, setReviewerNotes] = useState(
    annotation?.reviewer_notes ?? "",
  );
  const [selectedTags, setSelectedTags] = useState<EvalFailureModeTag[]>(
    annotation?.failure_mode_tags ?? [],
  );

  const status = annotation?.status;
  const showCreate = canCreateAnnotation(status);
  const showStartReview = canStartReview(status);
  const showReviewActions = canApproveOrReject(status);
  const isTerminal = isTerminalAnnotationStatus(status);
  const isApproved = status === EvalAnnotationStatusValues.approved;
  const showReviewDetails =
    Boolean(annotation) &&
    (showReviewActions ||
      isTerminal ||
      status === EvalAnnotationStatusValues.approved);

  function toggleTag(tag: EvalFailureModeTag) {
    setSelectedTags((current) =>
      current.includes(tag)
        ? current.filter((value) => value !== tag)
        : [...current, tag],
    );
  }

  function handleApprove() {
    onApprove({
      reviewerNotes: reviewerNotes.trim(),
      failureModeTags: selectedTags,
    });
  }

  function handleReject() {
    onReject({
      reviewerNotes: reviewerNotes.trim(),
      failureModeTags: selectedTags,
    });
  }

  if (isLoading) {
    return (
      <div className={styles.panel} data-testid="annotation-review-panel">
        <p className={styles.message}>Loading annotation…</p>
      </div>
    );
  }

  return (
    <div className={styles.panel} data-testid="annotation-review-panel">
      <div className={styles.headerRow}>
        <h2 className={styles.title}>Annotation</h2>
        <AnnotationStatusBadge status={status} />
      </div>

      {annotation && (
        <dl className={styles.metaList}>
          <div>
            <dt>Annotation ID</dt>
            <dd>{annotation.annotation_id}</dd>
          </div>
          {annotation.reviewer_assigned && (
            <div>
              <dt>Reviewer</dt>
              <dd>{annotation.reviewer_assigned}</dd>
            </div>
          )}
          {annotation.sanitised != null && (
            <div>
              <dt>Sanitised</dt>
              <dd>{annotation.sanitised ? "Yes" : "No"}</dd>
            </div>
          )}
          {annotation.exported != null && (
            <div>
              <dt>Exported</dt>
              <dd>{annotation.exported ? "Yes" : "No"}</dd>
            </div>
          )}
        </dl>
      )}

      {errorMessage && (
        <p className={styles.error} data-testid="annotation-review-error">
          {errorMessage}
        </p>
      )}

      {showCreate && (
        <button
          type="button"
          className={styles.primaryButton}
          disabled={isSubmitting || isCreating || !reviewerIdentity}
          onClick={onCreateAndStartReview}
        >
          Create annotation & start review
        </button>
      )}

      {showStartReview && (
        <button
          type="button"
          className={styles.primaryButton}
          disabled={isSubmitting || !reviewerIdentity}
          onClick={onStartReview}
        >
          Start review
        </button>
      )}

      {showReviewDetails && annotation && (
        <div className={styles.reviewForm}>
          <label className={styles.field}>
            <span className={styles.label}>Reviewer notes</span>
            <textarea
              className={styles.textarea}
              value={reviewerNotes}
              onChange={(event) => setReviewerNotes(event.target.value)}
              rows={4}
              disabled={!showReviewActions || isSubmitting}
              aria-label="Reviewer notes"
            />
          </label>

          <fieldset className={styles.tagFieldset} disabled={!showReviewActions || isSubmitting}>
            <legend className={styles.label}>Failure mode tags</legend>
            <div className={styles.tagGrid}>
              {FAILURE_MODE_OPTIONS.map((option) => (
                <label key={option.value} className={styles.tagOption}>
                  <input
                    type="checkbox"
                    checked={selectedTags.includes(option.value)}
                    onChange={() => toggleTag(option.value)}
                    disabled={!showReviewActions || isSubmitting}
                  />
                  <span>{option.label}</span>
                </label>
              ))}
            </div>
          </fieldset>

          {showReviewActions && (
            <div className={styles.actionRow}>
              <button
                type="button"
                className={styles.primaryButton}
                disabled={isSubmitting}
                onClick={handleApprove}
              >
                Approve
              </button>
              <button
                type="button"
                className={styles.dangerButton}
                disabled={isSubmitting}
                onClick={handleReject}
              >
                Reject
              </button>
            </div>
          )}

          {isTerminal && (
            <p className={styles.terminalHint}>
              This annotation is in a terminal state and cannot be changed.
            </p>
          )}

          {isApproved && !isTerminal && (
            <p className={styles.terminalHint}>
              Annotation approved. Proceed to sanitisation below.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
